
import { supabase } from '@/lib/supabase';
import type { Json } from '@/integrations/supabase/types';

// Define better types for recordings
export interface RecordingData {
  id: string | number;
  title?: string | null;
  details?: string | null;
  video_path?: string | null;
  created_at?: string;
  user_id?: string;
  avatar_path?: string | null;
  emotion?: any;
  location?: any;
}

// Points related types
export interface PointsData {
  id: number;
  transaction_id: number;
  points_amt: number;
  user_id: string;
  created_at: string;
}

export interface PointsResponse {
  success: boolean;
  data?: PointsData[];
  error?: Error | string;
}

export interface PointsRedemptionData {
  id: number;
  user_id: string;
  transaction_amt?: number;
  points: number;
  share_Id?: number;
  created_at: string;
  transaction_ref?: any;
}

// Get the currently logged-in user
export const getCurrentUser = async () => {
  const { data, error } = await supabase.auth.getUser();
  if (error) {
    console.error("Error getting current user:", error.message);
    return null;
  }
  return data.user;
};

// Get user recordings
export const getUserRecordings = async (userId: string): Promise<RecordingData[]> => {
  try {
    const { data, error } = await supabase
      .from('recordings')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error("Error fetching recordings:", error);
      return [];
    }

    // Convert any JSON data into proper format
    const formattedData: RecordingData[] = data?.map(item => ({
      id: item.id,
      title: typeof item.title === 'string' ? item.title : 
             (item.title ? String(item.title) : null),
      details: typeof item.details === 'string' ? item.details :
              (item.details ? String(item.details) : null),
      video_path: typeof item.video_path === 'string' ? item.video_path : 
                 (item.video_path ? String(item.video_path) : null),
      created_at: item.created_at,
      user_id: item.user_id,
      avatar_path: typeof item.avatar_path === 'string' ? item.avatar_path :
                  (item.avatar_path ? String(item.avatar_path) : null),
      emotion: item.emotion,
      location: item.location
    })) || [];

    return formattedData;
  } catch (err) {
    console.error("Exception fetching recordings:", err);
    return [];
  }
};

// Save a new recording
export interface SaveRecordingParams {
  userId: string;
  title: string;
  description?: string;
  videoBlob: Blob;
  location?: {
    latitude: number;
    longitude: number;
    address: string;
  } | null;
  avatar?: string | null;
  emotionData?: any;
}

// Fixed interface to match what's expected in the function
export interface DatabaseRecordingParams {
  userId: string;
  title: string;
  details?: string;
  videoPath: string;
  avatarPath?: string;
  emotion?: any;
  location?: any;
}

// Check if storage buckets exist and create them if needed
export const ensureStorageBucketsExist = async () => {
  try {
    // Check if videos bucket exists
    const { data: videoBuckets, error: videosError } = await supabase.storage.listBuckets();
    
    if (videosError) {
      console.error("Error checking storage buckets:", videosError);
      return false;
    }
    
    const videosBucketExists = videoBuckets?.some(bucket => bucket.name === 'videos');
    const avatarsBucketExists = videoBuckets?.some(bucket => bucket.name === 'avatars');
    
    console.log("Storage buckets check:", {
      videosBucketExists,
      avatarsBucketExists,
      buckets: videoBuckets?.map(b => b.name)
    });
    
    // Create videos bucket if it doesn't exist
    if (!videosBucketExists) {
      const { error: createVideosError } = await supabase.storage.createBucket('videos', {
        public: true,
        fileSizeLimit: 100000000 // 100MB
      });
      
      if (createVideosError) {
        console.error("Error creating videos bucket:", createVideosError);
        return false;
      }
      
      console.log("Created videos bucket");
    }
    
    // Create avatars bucket if it doesn't exist
    if (!avatarsBucketExists) {
      const { error: createAvatarsError } = await supabase.storage.createBucket('avatars', {
        public: true,
        fileSizeLimit: 10000000 // 10MB
      });
      
      if (createAvatarsError) {
        console.error("Error creating avatars bucket:", createAvatarsError);
        return false;
      }
      
      console.log("Created avatars bucket");
    }
    
    return true;
  } catch (error) {
    console.error("Exception in ensureStorageBucketsExist:", error);
    return false;
  }
};

// Save a recording with camera - UPDATED to include full URL paths
export const saveRecordingForCamera = async (params: SaveRecordingParams) => {
  try {
    const { userId, title, description, videoBlob, location, avatar, emotionData } = params;
    
    if (!userId) {
      console.error("No user ID provided");
      return { success: false, error: "No user ID provided" };
    }

    if (!videoBlob || videoBlob.size === 0) {
      console.error("No video data provided");
      return { success: false, error: "No video data provided" };
    }

    console.log("Starting saveRecordingForCamera with params:", { 
      userId, title, description, 
      videoBlobSize: videoBlob.size,
      location, hasAvatar: !!avatar, emotionData 
    });

    // Ensure storage buckets exist before uploading
    await ensureStorageBucketsExist();

    // Generate a unique timestamp and filenames
    const timestamp = new Date().getTime();
    const videoFileName = `${userId}/${timestamp}_video.webm`;
    const avatarFileName = avatar ? `${userId}/${timestamp}_avatar.png` : null;
    
    console.log("Generated filenames:", videoFileName, avatarFileName);

    // Upload the video to Supabase storage
    const { data: videoData, error: videoError } = await supabase.storage
      .from('videos')
      .upload(videoFileName, videoBlob, {
        contentType: 'video/webm',
        cacheControl: '3600'
      });

    if (videoError) {
      console.error("Error uploading video:", videoError);
      return { success: false, error: videoError };
    }

    console.log("Video uploaded successfully:", videoData);
    
    // Get the full URL for the video
    const { data: videoUrlData } = await supabase.storage
      .from('videos')
      .getPublicUrl(videoData?.path || videoFileName);
      
    // Store the complete URL instead of just the path
    const fullVideoUrl = videoUrlData?.publicUrl;
    
    console.log("Generated full video URL:", fullVideoUrl);
    
    if (!fullVideoUrl) {
      console.error("Could not generate public URL for video");
      return { success: false, error: "Failed to generate video URL" };
    }
    
    // Upload the avatar if available
    let avatarPath = null;
    let fullAvatarUrl = null;
    
    if (avatar) {
      const avatarBlob = await (await fetch(avatar)).blob();
      const { data: avatarData, error: avatarError } = await supabase.storage
        .from('avatars')
        .upload(avatarFileName!, avatarBlob, {
          contentType: 'image/png',
          cacheControl: '3600'
        });

      if (avatarError) {
        console.error("Error uploading avatar:", avatarError);
        // Continue without avatar rather than failing
      } else {
        // Also get full URL for avatar
        const { data: avatarUrlData } = await supabase.storage
          .from('avatars')
          .getPublicUrl(avatarData?.path || avatarFileName!);
          
        fullAvatarUrl = avatarUrlData?.publicUrl;
        avatarPath = fullAvatarUrl || null;
        console.log("Avatar uploaded successfully, full URL:", fullAvatarUrl);
      }
    }

    // Prepare the data for the database with proper typing
    const recordingEntry = {
      user_id: userId,
      title: title || "Untitled Recording",
      details: description || null,
      // Store the FULL URL instead of just the path
      video_path: fullVideoUrl,
      avatar_path: avatarPath || null,
      emotion: emotionData || null,
      location: location || null,
      // Explicitly set created_at to now() to match database requirements
      created_at: new Date().toISOString()
    };

    console.log("Inserting recording into database with full URL path:", recordingEntry.video_path);

    // Insert the recording into the database with the correct typing
    const { data: dbData, error: dbError } = await supabase
      .from('recordings')
      .insert(recordingEntry)
      .select();

    if (dbError) {
      console.error("Error inserting recording:", dbError);
      return { success: false, error: dbError };
    }

    console.log("Recording saved successfully:", dbData);
    return { success: true, data: dbData };

  } catch (error) {
    console.error("Exception in saveRecordingForCamera:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error : new Error("Unknown error saving recording") 
    };
  }
};

// Get location from coordinates
export const getAddressFromCoordinates = async (latitude: number, longitude: number): Promise<string> => {
  try {
    const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
    const data = await response.json();
    return data.display_name || "Unknown location";
  } catch (error) {
    console.error("Error getting address:", error);
    return "Location unavailable";
  }
};

// Get video public URL - Completely rewritten to properly handle paths
export const getVideoPublicUrl = async (path: string | null): Promise<string> => {
  try {
    if (!path) {
      console.error("Empty path provided to getVideoPublicUrl");
      return '';
    }
    
    // Debug the raw path
    console.log("Raw video path received:", path);
    
    // Clean up path if it's in JSON format or contains quotes
    let cleanPath = '';
    
    // First try to parse if it's a JSON string
    if (typeof path === 'string') {
      if (path.startsWith('{') || path.startsWith('"')) {
        try {
          const parsed = JSON.parse(path);
          cleanPath = typeof parsed === 'string' ? parsed : JSON.stringify(parsed);
        } catch (e) {
          cleanPath = path;
        }
      } else {
        cleanPath = path;
      }
    } else if (path) {
      cleanPath = String(path);
    }
    
    // Remove any quotes that might be in the string from JSON parsing
    cleanPath = cleanPath.replace(/^"|"$/g, '');
    
    console.log("Cleaned video path:", cleanPath);
    
    // If path is already a URL, return it
    if (cleanPath.startsWith('http')) {
      console.log("Path is already a URL, returning as-is:", cleanPath);
      return cleanPath;
    }
    
    // Get public URL from Supabase
    // Note: getPublicUrl doesn't actually return an error property
    const { data } = await supabase.storage
      .from('videos')
      .getPublicUrl(cleanPath);
    
    if (!data || !data.publicUrl) {
      console.error("Failed to get public URL from Supabase for path:", cleanPath);
      return '';
    }
    
    console.log("Successfully generated public URL:", data.publicUrl);
    return data.publicUrl;
  } catch (error) {
    console.error("Error in getVideoPublicUrl:", error);
    return '';
  }
};

// Points related functions
export const issuePoints = async (
  userId: string, 
  pointsAmount: number, 
  transactionId: number
): Promise<{ success: boolean; error?: string }> => {
  try {
    if (!userId) {
      return { success: false, error: "No user ID provided" };
    }
    
    const { error } = await supabase
      .from('points')
      .insert([
        {
          user_id: userId,
          points_amt: pointsAmount,
          transaction_id: transactionId
        }
      ]);
      
    if (error) {
      console.error("Error issuing points:", error);
      return { success: false, error: error.message };
    }
    
    return { success: true };
  } catch (error) {
    console.error("Exception in issuePoints:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "Unknown error issuing points" 
    };
  }
};

// Get points history
export const getPointsHistory = async (userId: string): Promise<PointsResponse> => {
  try {
    const { data, error } = await supabase
      .from('points')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
      
    if (error) {
      console.error("Error fetching points history:", error);
      return { success: false, error };
    }
    
    return { success: true, data: data || [] };
  } catch (error) {
    console.error("Exception in getPointsHistory:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error : new Error("Unknown error fetching points history") 
    };
  }
};

// Get points redemption history
export const getPointsRedemptionHistory = async (userId: string, page: number = 1, pageSize: number = 10) => {
  try {
    // First get the count of all records
    const { count, error: countError } = await supabase
      .from('points_redemption')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);
      
    if (countError) {
      console.error("Error counting redemption history:", countError);
      return { success: false, error: countError, data: [], count: 0 };
    }
    
    // Calculate offset based on page and pageSize
    const offset = (page - 1) * pageSize;
    
    // Fetch the actual data for the page
    const { data, error } = await supabase
      .from('points_redemption')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .range(offset, offset + pageSize - 1);
      
    if (error) {
      console.error("Error fetching redemption history:", error);
      return { success: false, error, data: [], count: 0 };
    }
    
    return { 
      success: true, 
      data: data || [], 
      count: count || 0 
    };
  } catch (error) {
    console.error("Exception in getPointsRedemptionHistory:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error : new Error("Unknown error fetching redemption history"),
      data: [],
      count: 0 
    };
  }
};

// Social share related functions
export const addSocialShare = async (
  userId: string,
  videoId: string,
  platform: string,
  shareLink: string
): Promise<boolean> => {
  try {
    // Define default points for sharing
    const pointsForSharing = 5;
    
    // Insert the share record
    const { error } = await supabase
      .from('shares')
      .insert([
        {
          user_id: userId,
          video_id: videoId,
          platform,
          share_link: shareLink,
          points: pointsForSharing,
          success: true
        }
      ]);
      
    if (error) {
      console.error("Error adding social share:", error);
      return false;
    }
    
    // Update the user's points (simplified)
    const txId = Math.floor(Math.random() * 1000000);
    const pointsResult = await issuePoints(userId, pointsForSharing, txId);
    
    return pointsResult.success;
  } catch (error) {
    console.error("Exception in addSocialShare:", error);
    return false;
  }
};

// Get social shares
export const getSocialShares = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('shares')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
      
    if (error) {
      console.error("Error fetching social shares:", error);
      return { success: false, error, data: [] };
    }
    
    return { success: true, data: data || [] };
  } catch (error) {
    console.error("Exception in getSocialShares:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error : new Error("Unknown error fetching social shares"),
      data: [] 
    };
  }
};
